import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';  // Importera Helmet

const extraPrices = {
  flyttningAvMöbler: 400,
  garage: 400,
  källareVind: 300,
  bortforsling: 199,
  inglasadBalkong: 299,
};

export default function BookingForm() {
  const [formData, setFormData] = useState({
    token: '', // Lägg till detta
    name: '',
    email: '',
    phone: '',
    area: 0,
    basePrice: 0,
    personnummer: '',
    address: '',
    postalCode: '',
    date: '',
    message: '',
    extraServices: {
      flyttningAvMöbler: false,
      garage: false,
      källareVind: false,
      bortforsling: false,
      inglasadBalkong: false,
    },
  });

  const [status, setStatus] = useState(null);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const ref = query.get('ref');
    if (!ref) return;

    async function fetchBooking() {
      try {
        const res = await fetch(`https://provalt.se/backend/flytt/get-booking.php?ref=${ref}`);
        const data = await res.json();

        if (!res.ok || !data.success) {
          setStatus('Kunde inte hitta bokningsinformationen.');
          return;
        }

        setFormData(prev => ({
          ...prev,
          name: data.name,
          email: data.email,
          area: data.area,
          basePrice: data.price,
          token: ref, // Lägg till detta!
        }));
      } catch (err) {
        setStatus('Fel vid hämtning av bokningsinformation.');
      }
    }

    fetchBooking();
  }, []);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        extraServices: {
          ...prev.extraServices,
          [name]: checked,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  }

  const totalPrice = React.useMemo(() => {
    const extrasSum = Object.entries(formData.extraServices).reduce(
      (sum, [key, checked]) => (checked ? sum + (extraPrices[key] || 0) : sum),
      0
    );
    return formData.basePrice + extrasSum;
  }, [formData.basePrice, formData.extraServices]);

  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.personnummer || !formData.address || !formData.date || !formData.name || !formData.phone || !formData.postalCode) {
      setStatus('Vänligen fyll i alla obligatoriska fält.');
      return;
    }

    setStatus('Skickar bokning...');

    const payload = {
      ...formData,
      totalPrice,
    };

    try {
      const response = await fetch('https://provalt.se/backend/flytt/booking-confirmation.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const text = await response.text();
      if (response.ok) {
        navigate('/tack'); // 👈 Skicka till tack-sidan
      } else {
        setStatus(`Fel: ${text}`);
      }
    } catch {
      setStatus('Något gick fel. Försök igen senare.');
    }
  }

  // Kontrollera om vi har fel på bokningsinformationen
  const hasBookingError = status && (
    status.toLowerCase().includes('kunde inte hitta') ||
    status.toLowerCase().includes('fel vid hämtning')
  );

  return (
    <>
      <Helmet>
        <title>Boka din flyttstädning | Fast Pris | Kundnöjdhetsgaranti </title>
        <meta name="description" content="Professionell och noggrann flyttstädning för ditt hem eller kontor. Vi ser till att ditt gamla boende är skinande rent inför nästa hyresgäst eller köpare." />
        <link rel="canonical" href="https://provalt.se/boka" />
      </Helmet>
      <div style={styles.wrapper}>
        <h2 style={styles.heading}>Boka din flyttstädning</h2>
        <p>Fyll i dina uppgifter nedan för att boka din flyttstädning. Vi återkommer med en bekräftelse via mejl.</p>

        <form onSubmit={handleSubmit}>
          {/* FORM FIELDS */}
          <Input label="Namn*" name="name" value={formData.name} onChange={handleChange} required />
          <Input label="E-post" name="email" value={formData.email} disabled />
          <Input label="Telefonnummer*" name="phone" value={formData.phone} onChange={handleChange} required />
          <Input label="Adress*" name="address" value={formData.address} onChange={handleChange} required />
          <Input label="Postnummer*" name="postalCode" value={formData.postalCode} onChange={handleChange} required />
          <Input label="Personnummer*" name="personnummer" value={formData.personnummer} onChange={handleChange} required />
          <Input label="Datum för städning*" name="date" value={formData.date} type="date" onChange={handleChange} required />
          <Textarea label="Meddelande (valfritt):" name="message" value={formData.message} onChange={handleChange} />

          <fieldset style={styles.fieldset}>
            <legend>Extra tjänster (valfritt):</legend>
            {Object.keys(formData.extraServices).map(service => (
              <label key={service} style={{ display: 'block', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  name={service}
                  checked={formData.extraServices[service]}
                  onChange={handleChange}
                  style={{ marginRight: '6px' }}
                />
                {formatServiceName(service)}
              </label>
            ))}
          </fieldset>

          <p style={styles.total}>Pris inkl. tilläggstjänster: {totalPrice} :-</p>

          <button
            type="submit"
            style={{
              ...styles.button,
              cursor: hasBookingError ? 'not-allowed' : 'pointer',
              backgroundColor: hasBookingError ? '#aaa' : '#2E86C1',
            }}
            disabled={hasBookingError}
          >
            Boka
          </button>
        </form>

        {status && (
          <p
            style={{
              marginTop: '16px',
              textAlign: 'center',
              color: status.startsWith('Fel') || hasBookingError ? 'red' : 'green',
            }}
          >
            {status}
          </p>
        )}
      </div>
    </>
  );
}

// 🧩 Hjälpkomponenter
const Input = ({ label, name, value, onChange, required, disabled = false, type = 'text' }) => (
  <label>
    {label}
    <br />
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      style={styles.input(disabled)}
    />
  </label>
);

const Textarea = ({ label, name, value, onChange }) => (
  <label>
    {label}
    <br />
    <textarea name={name} value={value} onChange={onChange} rows={4} style={styles.textarea} />
  </label>
);

// 🔠 Tjänstnamn formattering
function formatServiceName(service) {
  const map = {
    flyttningAvMöbler: 'Flyttning av möbler',
    garage: 'Garage',
    källareVind: 'Källare/vind',
    bortforsling: 'Bortforsling & återvinning av sopor',
    inglasadBalkong: 'Inglasad balkong',
  };
  return map[service] || service;
}

// 🎨 Stilar
const styles = {
  wrapper: {
    maxWidth: '450px',
    margin: '40px auto',
    padding: '20px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f9f9f9',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  },
  heading: {
    textAlign: 'center',
    color: '#2E86C1',
  },
  input: (disabled) => ({
    width: '100%',
    padding: '8px',
    marginTop: 4,
    marginBottom: 12,
    borderRadius: 4,
    border: '1px solid #ccc',
    backgroundColor: disabled ? '#eee' : '#fff',
  }),
  textarea: {
    width: '100%',
    padding: '8px',
    marginTop: 4,
    marginBottom: 12,
    borderRadius: 4,
    border: '1px solid #ccc',
  },
  fieldset: {
    marginBottom: '16px',
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
  },
  total: {
    fontWeight: 'bold',
    fontSize: '1.2em',
    textAlign: 'center',
    marginBottom: '16px',
  },
  button: {
    width: '100%',
    backgroundColor: '#2E86C1',
    color: 'white',
    padding: '12px',
    fontSize: '16px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
};
